import { refreshToken } from './client';
import { useEffect, useState, useRef } from 'react';
import { decodeJwt } from 'jose';
import { safeEndSession } from './errorLink';
import { setAuthStore, getAuthStore } from './sessionStore';
import { useReportingMutations } from './siemReporterMutations';
import { Modal, Button } from 'react-bootstrap';
import { logger } from '../../lib/logger';

// How close to actual expiry counts as "stale" when we catch up after a tab wakes from
// background/sleep. Deliberately smaller than getRefreshDelay's own ~30s safety margin below -
// that margin is for the primary proactive schedule; this is a last-resort catch-up check for
// when that schedule was missed entirely, so it only needs to catch "already expired or expiring
// right now," not get ahead of it.
const WAKE_STALE_BUFFER_MS = 15000;

export const RefreshToken = () => {
  const { log } = useReportingMutations();
  const [showConfirm, setShowConfirm] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [awaitingRetry, setAwaitingRetry] = useState(false);

  // Prevents concurrent refresh calls from racing with the same token
  const isRefreshing = useRef(false);

  // Mirror of awaitingRetry, updated synchronously alongside setAwaitingRetry (see
  // updateAwaitingRetry below) so the self-rescheduling timeout loop and the visibility/focus
  // wake handler always see the current value without waiting on a render + effect round trip.
  const awaitingRetryRef = useRef(false);
  const updateAwaitingRetry = (value) => {
    awaitingRetryRef.current = value;
    setAwaitingRetry(value);
  };

  // Holds the id of the currently scheduled setTimeout so it can be cleared and re-armed -
  // both by the self-rescheduling loop below and by the visibility/focus wake check.
  const timeoutRef = useRef(null);

  // Disables the Retry button while a manual retry is in flight
  const [isRetrying, setIsRetrying] = useState(false);

  // Derives the next refresh delay from a token's exp claim
  // Refreshes at 80% of token lifetime, minimum 30 seconds before expiry
  // Falls back to 4 minutes if token is missing or undecodable
  const getRefreshDelay = (accessToken) => {
    try {
      const decoded = decodeJwt(accessToken);
      // Guard against missing or non-numeric exp which produces NaN
      // NaN in setInterval fires at 0ms and spins the event loop
      if (!Number.isFinite(decoded?.exp)) {
        return 240000;
      }

      const expiresIn = decoded.exp - Math.floor(Date.now() / 1000);
      // Guard against already-expired token producing a negative delay
      if (expiresIn <= 0) {
        return 240000;
      }

      const delay = Math.min(
        (expiresIn * 0.8) * 1000,
        (expiresIn - 30) * 1000
      );
      // Never schedule a refresh in the past or in less than 5 seconds
      return Math.max(delay, 5000);
    } catch {
      return 240000;
    }
  };

  // Lazy initializer — derives the first scheduled delay from the token already in storage
  // at mount time so the very first refresh fires at the correct time
  const [refreshTokenTime, setRefreshTokenTime] = useState(() => {
    const { access_token } = getAuthStore();
    return getRefreshDelay(access_token);
  });

  // Ref mirror of refreshTokenTime — the self-rescheduling setTimeout loop reads this
  // synchronously each time it re-arms itself, rather than depending on refreshTokenTime and
  // tearing down/recreating the whole effect (and its pending timer) on every tick the way the
  // previous setInterval-in-a-useEffect-with-refreshTokenTime-in-deps version did.
  const refreshTokenTimeRef = useRef(refreshTokenTime);
  const updateRefreshTokenTime = (value) => {
    refreshTokenTimeRef.current = value;
    setRefreshTokenTime(value);
  };

  // True if the token currently in storage is already expired, or will expire within
  // WAKE_STALE_BUFFER_MS. Used by the visibility/focus wake check below, not by the normal
  // schedule (getRefreshDelay already builds its own, larger safety margin into that).
  const isTokenStaleOrExpiring = () => {
    try {
      const { access_token } = getAuthStore();
      const decoded = decodeJwt(access_token);
      if (!Number.isFinite(decoded?.exp)) return true; // can't verify freshness - treat as stale
      const msRemaining = decoded.exp * 1000 - Date.now();
      return msRemaining <= WAKE_STALE_BUFFER_MS;
    } catch {
      return true;
    }
  };

  const processTokenResponse = (response) => {
    // Unrecoverable — token already rotated or revoked by Keycloak
    // No amount of retrying will fix this, sign out immediately
    if (response?.error === 'invalid_grant') {
      logger.error('Refresh token invalid or revoked — signing out');
      safeEndSession('refresh token invalid or expired');
      return 'invalid_grant';
    }

    if (response?.access_token) {
      // Guard against malformed token — treat as unrecoverable, sign out
      // rather than falling through to the generic retry modal
      try {
        const decoded = decodeJwt(response.access_token);

        if (!Number.isFinite(decoded?.exp)) {
          throw new Error('Token missing valid exp claim');
        }

        const iso = new Date(decoded.exp * 1000).toISOString().split('.')[0] + "Z";

        log({ variables: { event: {
          name: "portalTokenRefresh",
          field: "tokenExpiryTime",
          value: iso
        }}});

        // Update the scheduled delay based on the newly issued token's expiry
        updateRefreshTokenTime(getRefreshDelay(response.access_token));
        setAuthStore(Object.assign({}, response));
        return 'success';
      } catch {
        // Token present but undecodable or missing exp — unrecoverable
        logger.error('Refreshed token is malformed — signing out');
        safeEndSession('malformed token after refresh');
        return 'invalid_grant';
      }
    }

    // Network or unknown error — modal retry is reasonable
    return 'error';
  };

  const handleRetry = async () => {
    // Guard against rapid clicks firing multiple concurrent retries
    if (isRetrying) return;
    setIsRetrying(true);

    // Functional update — safe against stale closure if user clicks rapidly
    // currentAttempt is derived inside the updater to always reflect current state
    let currentAttempt;
    setRetryCount(c => {
      currentAttempt = c + 1;
      return currentAttempt;
    });

    try {
      const response = await refreshToken();
      const result = processTokenResponse(response);

      if (result === 'success') {
        setShowConfirm(false);
        updateAwaitingRetry(false);
        setRetryCount(0);
        setIsRetrying(false);
        // The scheduled loop skipped every tick while awaitingRetry was true (it just
        // rescheduled itself on the old cadence without touching the timer), so re-arm now
        // against the fresh delay processTokenResponse just derived instead of waiting for
        // whatever's left of that stale interval to elapse.
        scheduleRefresh(refreshTokenTimeRef.current);
        return;
      }

      if (result === 'invalid_grant') {
        // Already handled in processTokenResponse — just close modal
        setShowConfirm(false);
        updateAwaitingRetry(false);
        setRetryCount(0);
        setIsRetrying(false);
        return;
      }
    } catch (err) {
      logger.error('Retry attempt failed');
    }

    setIsRetrying(false);

    // currentAttempt is set synchronously in the setRetryCount updater above
    if (currentAttempt >= 3) {
      setShowConfirm(false);
      updateAwaitingRetry(false);
      setRetryCount(0);
      safeEndSession('access token missing from refresh');
    }
    // else: keep modal open, user can try again up to 3 times
  };

  const handleCancel = () => {
    safeEndSession('access token missing from refresh');
  };

  // Cancels whatever refresh is currently scheduled, without scheduling a new one.
  const clearScheduledRefresh = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Arms a single one-shot setTimeout for `delay` ms from now, replacing any previously
  // scheduled one. Deliberately setTimeout, not setInterval: setInterval keeps firing on a
  // fixed period regardless of how long each tick's own async work took, and — per the
  // confirmed browser behavior behind this bug — background tabs and OS sleep can throttle or
  // fully suspend a *long-lived repeating* timer far more aggressively than a short-lived
  // one-shot timer that gets re-armed fresh after each attempt actually completes. Recreating
  // the timer from scratch each cycle also means a single slow/hung refreshToken() call can't
  // leave two ticks racing each other the way an interval firing on a fixed clock could.
  const scheduleRefresh = (delay) => {
    clearScheduledRefresh();
    timeoutRef.current = setTimeout(runScheduledRefresh, delay);
  };

  // The actual refresh attempt, shared by the normal schedule and the visibility/focus wake
  // check below - split out from scheduling so either caller can invoke it without also
  // committing to "and then wait refreshTokenTime before trying again."
  const attemptRefresh = async () => {
    // Skip if a refresh is already in flight or the user is handling the retry modal
    if (isRefreshing.current || awaitingRetryRef.current) return;

    isRefreshing.current = true;

    try {
      const response = await refreshToken();
      const result = processTokenResponse(response);

      if (result === 'error') {
        // Network or unknown failure — show modal, retry is reasonable
        log({ variables: { event: {
          name: "portalTokenRefreshError",
          field: "missingAccessToken",
          value: "No access token in response. Prompting user to retry."
        }}});
        updateAwaitingRetry(true);
        setRetryCount(0);
        setShowConfirm(true);
      }
      // 'success' and 'invalid_grant' are handled in processTokenResponse
    } catch (error) {
      // Pure network exception — Keycloak never received the request
      // Retry is reasonable since the token was not consumed
      updateAwaitingRetry(true);
      setRetryCount(0);
      setShowConfirm(true);
    } finally {
      // Always release the lock whether success, failure, or exception
      isRefreshing.current = false;
    }
  };

  // One tick of the normal proactive-refresh schedule: attempt, then always re-arm for the
  // next cycle using whatever delay is current at that moment (processTokenResponse updates
  // refreshTokenTimeRef synchronously on a successful refresh, so a just-renewed token's own
  // expiry drives the next wait, not the previous token's).
  const runScheduledRefresh = async () => {
    await attemptRefresh();
    scheduleRefresh(refreshTokenTimeRef.current);
  };

  useEffect(() => {
    scheduleRefresh(refreshTokenTimeRef.current);
    return () => clearScheduledRefresh();
    // Mount-once: the loop above re-arms itself via scheduleRefresh/runScheduledRefresh using
    // refs, so it doesn't need (and must not have) refreshTokenTime/awaitingRetry as deps - that
    // was what forced the old setInterval to be torn down and recreated on every single change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visibility/focus wake check: a scheduled timer - setInterval before, setTimeout now - can
  // be throttled or fully suspended by the browser while a tab is backgrounded or the OS is
  // asleep, so a refresh that was due can silently be skipped for as long as the tab stays
  // inactive. Rather than trusting that the schedule fired on time, check the token's actual
  // freshness the moment the tab becomes active again and catch up immediately if needed.
  useEffect(() => {
    const checkOnWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isTokenStaleOrExpiring()) return;
      if (isRefreshing.current || awaitingRetryRef.current) return;

      attemptRefresh().then(() => scheduleRefresh(refreshTokenTimeRef.current));
    };

    document.addEventListener('visibilitychange', checkOnWake);
    window.addEventListener('focus', checkOnWake);

    // Also covers the token already being stale the moment this component mounts (e.g. the
    // portal tab was reopened, or restored from a suspended/backgrounded state) rather than
    // only catching up on the *next* visibility change from here on.
    checkOnWake();

    return () => {
      document.removeEventListener('visibilitychange', checkOnWake);
      window.removeEventListener('focus', checkOnWake);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={showConfirm}
        onHide={handleCancel}
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Session Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p>A network issue has occurred while attempting to automatically refresh your session. This may be a temporary issue.</p>
            <div className="mt-3">Would you like to retry?</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end w-100">
            {retryCount > 0 && (
              <div className="d-flex me-3 align-items-end">
                <small>Attempts: {retryCount} / 3</small>
              </div>
            )}
            <Button onClick={handleCancel} variant="secondary">Sign Out</Button>
            {/* Disabled while retry is in flight — prevents rapid clicks miscounting attempts */}
            <Button onClick={handleRetry} className="ms-2" disabled={isRetrying}>
              {isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RefreshToken;
